const DESKTOP_MEDIA_QUERY = window.matchMedia('(min-width: 990px)');

class AveroakProductMediaSticky {
  constructor(grid) {
    this.grid = grid;
    this.section = grid.closest('.product-information');
    this.sectionRoot = this.section?.closest('.shopify-section') || null;
    this.media = grid.querySelector('.product-information__media');
    this.sticky = grid.querySelector('.product-information__media-sticky');
    this.gallery = this.sticky?.querySelector('media-gallery') || null;
    this.details = grid.querySelector('.product-details');
    this.raf = null;
    this.resizeObserver = null;
    this.boundarySection = null;

    this.requestUpdate = this.requestUpdate.bind(this);
    this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
  }

  init() {
    if (!this.section || !this.media || !this.sticky || this.grid.querySelector('.media-gallery--extend')) {
      return;
    }

    DESKTOP_MEDIA_QUERY.addEventListener('change', this.handleBreakpointChange);
    window.addEventListener('scroll', this.requestUpdate, { passive: true });
    window.addEventListener('resize', this.requestUpdate);

    this.boundarySection = this.findBoundarySection();

    this.resizeObserver = new ResizeObserver(() => this.requestUpdate());
    this.resizeObserver.observe(this.section);
    if (this.boundarySection && this.boundarySection !== this.section) {
      this.resizeObserver.observe(this.boundarySection);
    }
    this.resizeObserver.observe(this.media);
    this.resizeObserver.observe(this.sticky);
    if (this.details) {
      this.resizeObserver.observe(this.details);
    }
    if (this.gallery) {
      this.resizeObserver.observe(this.gallery);
    }

    this.handleBreakpointChange();
  }

  getOffset() {
    const headerComponent = document.querySelector('#header-component');
    const headerGroup = document.querySelector('#header-group');
    const cssHeaderHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    const headerHeight = Math.max(
      headerComponent?.getBoundingClientRect().height || 0,
      headerGroup?.getBoundingClientRect().height || 0,
      cssHeaderHeight
    );
    return headerHeight + 16;
  }

  handleBreakpointChange() {
    if (!DESKTOP_MEDIA_QUERY.matches) {
      this.reset();
      return;
    }

    this.requestUpdate();
  }

  findBoundarySection() {
    return this.section;
  }

  requestUpdate() {
    if (this.raf) {
      return;
    }

    this.raf = window.requestAnimationFrame(() => {
      this.raf = null;
      this.update();
    });
  }

  update() {
    if (!DESKTOP_MEDIA_QUERY.matches || !this.section || !this.media || !this.sticky || !this.details) {
      this.reset();
      return;
    }

    const mediaRect = this.media.getBoundingClientRect();
    const boundaryTarget = this.boundarySection || this.section;
    const boundaryRect = boundaryTarget.getBoundingClientRect();
    const stickyHeight = Math.ceil(this.sticky.getBoundingClientRect().height);
    const mediaWidth = Math.ceil(mediaRect.width);
    const offset = this.getOffset();

    if (!mediaWidth || !stickyHeight) {
      this.reset();
      return;
    }

    const mediaTop = mediaRect.top + window.scrollY;
    const boundaryBottom = boundaryRect.bottom + window.scrollY;
    const startPin = mediaTop - offset;
    const stopPin = boundaryBottom - stickyHeight - offset;

    if (stopPin <= startPin + 24) {
      this.reset();
      return;
    }

    this.media.style.setProperty('position', 'relative', 'important');
    this.media.style.setProperty('top', 'auto', 'important');
    this.media.style.setProperty('min-height', `${stickyHeight}px`, 'important');
    this.media.style.setProperty('overflow', 'visible', 'important');
    if (this.gallery) {
      this.gallery.style.setProperty('position', 'relative', 'important');
      this.gallery.style.setProperty('top', 'auto', 'important');
    }

    if (window.scrollY <= startPin) {
      this.renderStatic();
      return;
    }

    const top = offset - Math.max(window.scrollY - stopPin, 0);
    this.renderPinned(mediaRect, top);
  }

  renderStatic() {
    this.sticky.style.setProperty('position', 'static', 'important');
    this.sticky.style.setProperty('top', 'auto', 'important');
    this.sticky.style.setProperty('bottom', 'auto', 'important');
    this.sticky.style.setProperty('left', 'auto', 'important');
    this.sticky.style.setProperty('width', '100%', 'important');
    this.sticky.style.removeProperty('z-index');
  }

  renderPinned(mediaRect, top) {
    this.sticky.style.setProperty('position', 'fixed', 'important');
    this.sticky.style.setProperty('top', `${top}px`, 'important');
    this.sticky.style.setProperty('bottom', 'auto', 'important');
    this.sticky.style.setProperty('left', `${mediaRect.left}px`, 'important');
    this.sticky.style.setProperty('width', `${mediaRect.width}px`, 'important');
    this.sticky.style.setProperty('z-index', '2', 'important');
  }

  reset() {
    if (!this.media || !this.sticky) {
      return;
    }

    this.media.style.removeProperty('position');
    this.media.style.removeProperty('top');
    this.media.style.removeProperty('min-height');
    this.media.style.removeProperty('overflow');

    this.sticky.style.removeProperty('position');
    this.sticky.style.removeProperty('top');
    this.sticky.style.removeProperty('bottom');
    this.sticky.style.removeProperty('left');
    this.sticky.style.removeProperty('width');
    this.sticky.style.removeProperty('z-index');
    if (this.gallery) {
      this.gallery.style.removeProperty('position');
      this.gallery.style.removeProperty('top');
    }
  }
}

const initAveroakProductMediaSticky = () => {
  document.querySelectorAll('.product-information__grid:not(.product-information--media-none)').forEach((grid) => {
    if (grid.dataset.averoakMediaStickyInitialized === 'true') {
      return;
    }

    grid.dataset.averoakMediaStickyInitialized = 'true';
    new AveroakProductMediaSticky(grid).init();
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAveroakProductMediaSticky, { once: true });
} else {
  initAveroakProductMediaSticky();
}
